import { useEffect, useRef, useState } from 'react';
import type { Organization, Project } from '../lib/data';
import { contextDisplayLabel, shouldShowContextSwitcher } from '../lib/context-switcher';

interface ContextProps {
  orgs: readonly Organization[];
  orgId: string;
  projects: readonly Project[];
  projectId: string;
  onOrg: (id: string) => void;
  onProject: (id: string) => void;
}

function ContextFields({ orgs, orgId, projects, projectId, onOrg, onProject }: ContextProps) {
  const organization = orgs.find((item) => item.id === orgId) ?? null;
  const project = projects.find((item) => item.id === projectId) ?? null;

  return (
    <div className="context-fields">
      {orgs.length > 1 ? (
        <label className="context-field">
          <span>Organization</span>
          <select aria-label="Organization" value={orgId} onChange={(event) => onOrg(event.target.value)}>
            {orgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
        </label>
      ) : (
        <div className="context-field">
          <small>Organization</small>
          <strong>{organization?.name ?? 'Unknown'}</strong>
        </div>
      )}

      {projects.length > 1 ? (
        <label className="context-field">
          <span>Project</span>
          <select aria-label="Project" value={projectId} onChange={(event) => onProject(event.target.value)}>
            {projects.map((projectOption) => <option key={projectOption.id} value={projectOption.id}>{projectOption.name}</option>)}
          </select>
        </label>
      ) : (
        <div className="context-field">
          <small>Current project</small>
          <strong>{project?.name ?? 'No project selected'}</strong>
        </div>
      )}
    </div>
  );
}

export function ContextSettings(props: ContextProps) {
  return (
    <>
      <ContextFields {...props} />
      <small className="muted context-settings-note">
        Brunel remembers this working context. A compact top-bar switcher appears only when another organization or project is available.
      </small>
    </>
  );
}

export function ContextSwitcher(props: ContextProps) {
  const { orgs, orgId, projects, projectId } = props;
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  const organization = orgs.find((item) => item.id === orgId) ?? null;
  const project = projects.find((item) => item.id === projectId) ?? null;
  const label = contextDisplayLabel(organization?.name ?? null, project?.name ?? null);
  const available = shouldShowContextSwitcher(orgs.length, projects.length);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (root.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (!available && open) setOpen(false);
  }, [available, open]);

  if (!available) return null;

  return (
    <div className="context-switcher" ref={root}>
      <button
        ref={trigger}
        type="button"
        className="context-switcher-trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Switch organization or project. Current context: ${label}`}
        title={label}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="context-switcher-label">{label}</span>
        <span className="context-switcher-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="context-switcher-popover" role="dialog" aria-label="Switch organization or project">
          <ContextFields {...props} />
        </div>
      )}
    </div>
  );
}
